import { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { createTicket, createTicketMessage } from "services/ticketService";
import { getUserContacts } from "services/contactService";
import { AuthContext } from "context";

function TicketForm({ onSubmit, onCancel }) {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [formData, setFormData] = useState({
    subject: "",
    description: "",
    priority: "normal",
    kontaktId: "",
    firstMessage: "", // Neue Eigenschaft für die erste Nachricht
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      const userContacts = await getUserContacts();
      setContacts(userContacts);
    } catch (error) {
      console.error("Fehler beim Laden der Kontakte:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.subject.trim()) {
      newErrors.subject = "Betreff ist erforderlich";
    }
    // Beschreibung ist jetzt optional
    
    // Erste Nachricht ist verpflichtend
    if (!formData.firstMessage.trim()) {
      newErrors.firstMessage = "Erste Nachricht ist erforderlich";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      
      // Ticket erstellen
      const ticketData = {
        subject: formData.subject,
        description: formData.description || "Keine Beschreibung", // Fallback-Wert
        priority: formData.priority,
        kontaktId: formData.kontaktId || null
      };
      
      const ticket = await createTicket(ticketData);
      
      // Erste Nachricht erstellen
      if (ticket && ticket.id) {
        await createTicketMessage({
          ticketId: ticket.id,
          message: formData.firstMessage,
          senderType: 1, // 1 für Benutzer, 2 für Support-Mitarbeiter
          senderId: user?.id
        });
      }
      
      onSubmit(ticket);
    } catch (error) {
      console.error("Fehler beim Erstellen des Tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <SoftBox p={3}>
        <SoftTypography variant="h5" fontWeight="medium">
          Neues Support-Ticket
        </SoftTypography>
      </SoftBox>
      <SoftBox component="form" role="form" onSubmit={handleSubmit} p={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.subject}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Betreff<span style={{ color: "red" }}>*</span>
                </SoftTypography>
              </SoftBox>
              <SoftInput
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                error={!!errors.subject}
              />
              {errors.subject && (
                <SoftTypography variant="caption" color="error">
                  {errors.subject}
                </SoftTypography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Kontakt
                </SoftTypography>
              </SoftBox>
              <SoftSelect
                name="kontaktId"
                value={formData.kontaktId}
                onChange={handleChange}
                placeholder="Kontakt auswählen"
              >
                <MenuItem value="">Kein Kontakt</MenuItem>
                {contacts.map((contact) => (
                  <MenuItem key={contact.id} value={contact.id}>
                    {`${contact.Vorname} ${contact.Nachname}`}
                  </MenuItem>
                ))}
              </SoftSelect>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Priorität
                </SoftTypography>
              </SoftBox>
              <SoftSelect
                name="priority"
                value={formData.priority}
                onChange={handleChange}
              >
                <MenuItem value="niedrig">Niedrig</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="hoch">Hoch</MenuItem>
                <MenuItem value="kritisch">Kritisch</MenuItem>
              </SoftSelect>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Beschreibung (optional)
                </SoftTypography>
              </SoftBox>
              <SoftInput
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </FormControl>
          </Grid>
          
          {/* Neue erste Nachricht - verpflichtend */}
          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.firstMessage}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Erste Nachricht<span style={{ color: "red" }}>*</span>
                </SoftTypography>
              </SoftBox>
              <SoftInput
                name="firstMessage"
                value={formData.firstMessage}
                onChange={handleChange}
                multiline
                rows={4}
                error={!!errors.firstMessage}
                placeholder="Bitte beschreiben Sie Ihr Anliegen detailliert..."
              />
              {errors.firstMessage && (
                <SoftTypography variant="caption" color="error">
                  {errors.firstMessage}
                </SoftTypography>
              )}
            </FormControl>
          </Grid>
        </Grid>

        <SoftBox mt={4} mb={1} display="flex" justifyContent="space-between">
          <SoftButton variant="gradient" color="secondary" onClick={onCancel}>
            Abbrechen
          </SoftButton>
          <SoftButton
            type="submit"
            variant="gradient"
            color="info"
            disabled={loading}
          >
            {loading ? "Wird erstellt..." : "Ticket erstellen"}
          </SoftButton>
        </SoftBox>
      </SoftBox>
    </Card>
  );
}

TicketForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default TicketForm;